import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";

/**
 * BirthdayModal (owner 2026-07-23) — центрований попап для дня народження.
 *
 * Owner 2026-09-25: змінити вже вказану дату можна ЛИШЕ ОДИН РАЗ (так він
 * просив ще 07.09), далі — через підтримку; ліміт тримає сервер.
 *
 * Owner 2026-09-09: дату людина міняє САМА. Раніше вона задавалась один раз, а
 * далі йшов запит адміну — і власник міняв її руками. Від накрутки подарунка
 * захищає не незмінність, а правило «дата стоїть щонайменше 30 днів до самого
 * дня»; воно працює однаково, хто б її не вписав.
 *
 * Ввід — три акуратні селекти (День / Місяць / Рік): без «поплившого» нативного
 * date-інпута, без невалідних дат. Стиль 1:1 з іншими попапами (rounded-3xl,
 * анімований 🎂 з Telegram-набору).
 */

const MONTHS_UK = [
  "січня", "лютого", "березня", "квітня", "травня", "червня",
  "липня", "серпня", "вересня", "жовтня", "листопада", "грудня",
];

const SELECT_CLS =
  "w-full appearance-none bg-bg rounded-xl px-2.5 py-3 text-[0.875rem] text-ink " +
  "text-center focus:outline-none focus:ring-2 focus:ring-primary/30 " +
  "[&:invalid]:text-ink-faint";

export function BirthdayModal({
  onClose,
  onSave,
  saving,
  current,
}: {
  onClose: () => void;
  onSave: (isoDate: string) => void;
  saving: boolean;
  /** Уже вказана дата (YYYY-MM-DD) — тоді попап відкривається на ній. */
  current?: string | null;
}) {
  const nowYear = new Date().getFullYear();
  const cur = current ? current.split("-") : null;
  const [day, setDay] = useState(cur ? String(Number(cur[2])) : "");
  const [month, setMonth] = useState(cur ? String(Number(cur[1])) : ""); // 1..12
  const [year, setYear] = useState(cur ? cur[0] : "");

  const years = useMemo(
    () => Array.from({ length: nowYear - 1924 + 1 }, (_, i) => nowYear - i),
    [nowYear],
  );

  // Валідна дата (враховує лютий/30-31)?
  const iso = useMemo(() => {
    if (!day || !month || !year) return null;
    const d = Number(day), m = Number(month), y = Number(year);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d)
      return null; // напр. 31 лютого
    if (dt > new Date()) return null; // не в майбутньому
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }, [day, month, year]);

  const canSave = iso !== null && !saving;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xs bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />
        <div className="text-center mb-4">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="🎂" size={48} />
          </div>
          <div className="font-display text-[1.125rem] font-bold text-ink leading-tight mb-1.5 [text-wrap:balance]">
            {current ? "Зміни дату народження" : "Коли в тебе день народження?"}
          </div>
          <div className="text-[0.8125rem] text-ink-dim leading-relaxed [text-wrap:balance]">
            {current ? (
              <>
                Змінити дату можна <b className="text-ink">лише один раз</b> —
                перевір, що вона правильна.
              </>
            ) : (
              <>
                У твій день на тебе чекатиме{" "}
                <b className="text-ink">приємний подарунок</b> від нас. Це
                сюрприз.
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <select
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className={SELECT_CLS}
            required
          >
            <option value="" disabled hidden>
              День
            </option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={SELECT_CLS}
            required
          >
            <option value="" disabled hidden>
              Місяць
            </option>
            {MONTHS_UK.map((name, i) => (
              <option key={i} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className={SELECT_CLS}
            required
          >
            <option value="" disabled hidden>
              Рік
            </option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-2 text-center text-[0.6875rem] text-ink-faint">
          Напр. 15 · березня · 1995
        </div>

        <button
          type="button"
          disabled={!canSave}
          onClick={() => iso && onSave(iso)}
          className={
            "mt-4 block w-full rounded-full py-3.5 font-display text-[0.9375rem] font-semibold transition " +
            (canSave
              ? "bg-primary text-white active:bg-primary-deep"
              : "bg-ink/10 text-ink-dim cursor-not-allowed")
          }
        >
          {saving ? "Зберігаю…" : "Зберегти"}
        </button>

        {/* Правило 30 днів (owner 2026-07-24, перенесено ПІД кнопку
            2026-07-30) — щоб не розривало блок «поля → кнопка». */}
        <div className="mt-2.5 text-center text-[0.6875rem] text-ink-faint leading-snug [text-wrap:balance]">
          Подарунок діє, якщо вказати дату за 30+ днів до дня народження.
        </div>
      </div>
    </div>,
    document.body,
  );
}
